import { ENGLISH_LANGUAGE_CODES, SPANISH_LANGUAGE_CODES } from "@regal-voice/regal-client";

export type VoiceLanguageOption = {
    label: string;
    value: string;
    tooltip?: string;
};

export const MULTILINGUAL_LANGUAGE_TOOLTIP =
    "Supports English, Spanish, French, German, Hindi, Italian, Japanese, Dutch, Russian, and Portuguese conversations.";

/** Shared agent-language codes used by the builder Language dropdown. */
export const VOICE_LANGUAGE_OPTIONS: VoiceLanguageOption[] = [
    { label: "Bulgarian", value: "bg" },
    { label: "Danish", value: "da" },
    { label: "Dutch", value: "nl" },
    { label: "English (United States)", value: "en-US" },
    { label: "English (United Kingdom)", value: "en-GB" },
    { label: "English (India)", value: "en-IN" },
    { label: "Finnish", value: "fi" },
    { label: "French", value: "fr" },
    { label: "French (Canada)", value: "fr-CA" },
    { label: "German", value: "de" },
    { label: "German (Switzerland)", value: "de-CH" },
    { label: "Greek", value: "el" },
    { label: "Hindi", value: "hi" },
    { label: "Hungarian", value: "hu" },
    { label: "Indonesian", value: "id" },
    { label: "Italian", value: "it" },
    { label: "Japanese", value: "ja" },
    { label: "Korean", value: "ko" },
    { label: "Malay", value: "ms" },
    { label: "Multilingual", value: "multi", tooltip: MULTILINGUAL_LANGUAGE_TOOLTIP },
    { label: "Norwegian", value: "no" },
    { label: "Polish", value: "pl" },
    { label: "Portuguese", value: "pt" },
    { label: "Portuguese (Brazil)", value: "pt-BR" },
    { label: "Portuguese (Portugal)", value: "pt-PT" },
    { label: "Romanian", value: "ro" },
    { label: "Russian", value: "ru" },
    { label: "Slovak", value: "sk" },
    { label: "Spanish", value: "es" },
    { label: "Spanish (Latin America)", value: "es-419" },
    { label: "Swedish", value: "sv" },
    { label: "Turkish", value: "tr" },
    { label: "Vietnamese", value: "vi" },
];

/**
 * Admin language-compatibility options: English and Spanish are collapsed to one
 * code each, and Multilingual is omitted. Other builder languages stay as-is.
 */
export const VOICE_LANGUAGE_COMPATIBILITY_OPTIONS: VoiceLanguageOption[] = (() => {
    const options: VoiceLanguageOption[] = [];
    let hasEnglish = false;
    let hasSpanish = false;

    for (const option of VOICE_LANGUAGE_OPTIONS) {
        if (ENGLISH_LANGUAGE_CODES.has(option.value)) {
            if (!hasEnglish) {
                options.push({ label: "English", value: "en" });
                hasEnglish = true;
            }
            continue;
        }
        if (SPANISH_LANGUAGE_CODES.has(option.value)) {
            if (!hasSpanish) {
                options.push({ label: "Spanish", value: "es" });
                hasSpanish = true;
            }
            continue;
        }
        if (option.value === "multi") {
            continue;
        }
        options.push(option);
    }

    return options;
})();

export const ALL_LANGUAGES_LABEL = "All languages";

export function normalizeLanguageCompatibility(value?: string | null): string | undefined {
    if (!value) {
        return undefined;
    }
    if (ENGLISH_LANGUAGE_CODES.has(value)) {
        return "en";
    }
    if (SPANISH_LANGUAGE_CODES.has(value)) {
        return "es";
    }
    if (value === "multi") {
        return undefined;
    }
    return value;
}

export function normalizeLanguageCompatibilityList(value?: string | string[] | null): string[] {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    return [
        ...new Set(
            values.map((item) => normalizeLanguageCompatibility(item)).filter((item): item is string => Boolean(item))
        ),
    ];
}

export function getLanguageCompatibilityLabel(value?: string | string[] | null): string {
    const codes = normalizeLanguageCompatibilityList(value);
    if (codes.length === 0) {
        return ALL_LANGUAGES_LABEL;
    }
    return codes
        .map((code) => VOICE_LANGUAGE_COMPATIBILITY_OPTIONS.find((option) => option.value === code)?.label ?? code)
        .join(", ");
}
